// =============================================================================
// PostHog analytics — typed wrapper для product events.
//
// Принципы:
//   • EU host (eu.i.posthog.com) — данные не покидают ЕС
//   • persistence: localStorage — TG WebView blok'ает 3rd-party cookies
//   • autocapture: false — DOM содержит initData / tx_hash, autocapture
//                          мог бы их случайно отправить через DOM-attrs
//   • person_profiles: 'identified_only' — НЕ создаём anon profiles, persons
//                                          появляются только после identify(tg_id)
//   • disable_session_recording: true — privacy + free-tier quota
//   • before_send: strip tx_hash / initData из properties (defence-in-depth)
//
// distinct_id = telegram user.id (число). Username/first_name не отправляем.
// =============================================================================

import type { PostHogInterface, Properties } from 'posthog-js';

const POSTHOG_KEY = 'phc_tSoKai9jsB7pukzXAc3VaTAMy7Fx9u4phQihpDCj6KCx';
const POSTHOG_HOST = 'https://eu.i.posthog.com';

// Lazy-loaded PostHog instance. Init происходит только в browser, после
// `posthog-js` импорта в PostHogProvider.
let phInstance: PostHogInterface | null = null;

export function setPostHogInstance(ph: PostHogInterface): void {
  phInstance = ph;
}

export function getPostHogConfig() {
  return { key: POSTHOG_KEY, host: POSTHOG_HOST };
}

// 64-char hex strings (tx hashes). Не точное совпадение — может быть в
// середине string в URL или message.
const TX_HASH_RE = /\b[0-9a-f]{64}\b/gi;
// Telegram initData — длинная query-like строка с auth_date= и hash=.
const INIT_DATA_RE = /(auth_date=\d+&[^&\s]*hash=[0-9a-f]+|user=%7B%22id%22[^&\s]*)/gi;

function sanitizeValue(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  return v.replace(TX_HASH_RE, '[TX_HASH]').replace(INIT_DATA_RE, '[INIT_DATA]');
}

export function sanitizeProperties(props: Properties | undefined): Properties | undefined {
  if (!props) return props;
  const cleaned: Properties = {};
  for (const [k, v] of Object.entries(props)) {
    // Полностью убираем поля чьи имена выглядят сенситивно — даже если
    // value НЕ matches regex выше.
    if (/init.?data|tx.?hash|tx.?id|wallet.?address|seed|secret|token/i.test(k)) {
      cleaned[k] = '[REDACTED]';
      continue;
    }
    cleaned[k] = sanitizeValue(v);
  }
  return cleaned;
}

// =============================================================================
// Public API
// =============================================================================

/**
 * Identify пользователя по Telegram user.id. Username/first_name НЕ
 * отправляем как $name properties — privacy. Только language_code как
 * person property (нужен для marketing анализа аудитории по странам).
 */
export function identifyTgUser(
  tgUser: { id: number; language_code?: string } | null | undefined,
): void {
  if (!phInstance || !tgUser?.id) return;
  phInstance.identify(`tg:${tgUser.id}`, {
    language_code: tgUser.language_code ?? 'unknown',
  });
}

/**
 * Reset identity на logout / switch account. Очищает distinct_id и
 * persisted person properties.
 */
export function resetIdentity(): void {
  phInstance?.reset();
}

/**
 * Capture custom event с автосанитизацией properties.
 *
 * Список валидных событий — типизирован чтобы избежать опечаток и
 * расхождений в имени между местами вызова.
 */
type EventName =
  | 'app_opened'
  | 'onboarding_completed'
  | 'terms_accepted'
  | 'market_viewed'
  | 'bet_attempted'
  | 'bet_placed'
  | 'bet_failed'
  | 'deposit_screen_opened'
  | 'deposit_intent_created'
  | 'withdraw_screen_opened'
  | 'withdraw_requested'
  | 'dispute_filed'
  | 'profile_viewed'
  | 'leaderboard_viewed';

export function trackEvent(name: EventName, properties?: Properties): void {
  if (!phInstance) return;
  phInstance.capture(name, sanitizeProperties(properties));
}

/** Page view — manual, потому что autocapture off. */
export function trackPageView(path?: string): void {
  if (!phInstance) return;
  phInstance.capture('$pageview', {
    $current_url: path ?? (typeof window !== 'undefined' ? window.location.href : ''),
  });
}
